import { registerCommand, UNAVAILABLE_MESSAGE, viewerFlags, type CommandId } from '../commands';
import type { SeriesRef, StudyRef } from '../dicomweb';
import { fusionLayerInfo } from '../fusion/fusion';
import { instanceAt } from '../overlay';
import type { ViewerShell } from '../shell';
import { closeMenus, showMenu, type MenuItem } from '../ui/contextmenu';
import type { Viewport } from '../viewport';
import type { KeyImage, KeyImageIn } from './client';
import { hasPerm, isSessionStudy, PERM, risClient } from './session';
import { isMobileDevice } from '../device';

/**
 * Key images (spec sections 2 row 23 and 3): a star in each viewport's bottom-left overlay marks or
 * unmarks the image on screen, and the toolbar star lists the study's key images ("Elegido" on the one
 * shown, "No imágenes clave" when there are none); picking one shows it. Stored per study in ris.
 */

export const KEY_IMAGE_TEXT = {
  mark: 'Marcar como imagen clave',
  unmark: 'Quitar de las imágenes clave',
  chosen: 'Elegido',
  none: 'No imágenes clave',
  storeError: 'Lo siento, no fue posible almacenar la Imagen Clave.',
  deleteError: 'Lo siento, no fue posible borrar la Imagen Clave.',
  showError: 'Lo siento, no fue posible mostrar la Imagen Clave.',
  retrieveError: 'Lo siento, no fue posible recuperar la Imagen Clave.',
} as const;

const SVG_NS = 'http://www.w3.org/2000/svg';
const STAR_PATH = 'M12 2l2.9 6.9 7.1.6-5.4 4.7 1.6 7-6.2-3.8-6.2 3.8 1.6-7L2 9.5l7.1-.6z';
const DROPDOWN_GAP_PX = 2;

const byStudy = new Map<string, KeyImage[]>();
const loading = new Set<string>();
let shellRef: ViewerShell | null = null;
let busy = false;

/** Where the image on screen in `viewport` is: series, SOP instance, frame and stack position. */
function imageAddress(viewport: Viewport): { series: SeriesRef; sop: string; frame: number; index: number } | undefined {
  const stack = viewport.currentStack;
  const index = viewport.currentIndex();
  if (!stack || index === undefined || !viewport.currentImage()) return undefined;
  const instance = instanceAt(stack.series, index);
  if (!instance) return undefined;
  let before = 0;
  for (const other of stack.series.instances) {
    if (other === instance) break;
    before += other.numberOfFrames;
  }
  return { series: stack.series, sop: instance.sopInstanceUID, frame: index - before + 1, index };
}

function markedAt(viewport: Viewport): KeyImage | undefined {
  const address = imageAddress(viewport);
  if (!address) return undefined;
  return byStudy.get(address.series.studyInstanceUID)?.find((k) => k.sop_uid === address.sop && k.frame === address.frame);
}

// --- overlay star ------------------------------------------------------------------------------

/** IMEXHS hides the overlay star on mobile devices when `mobileModule` is on (spec section 7). */
let hiddenOnThisDevice = false;

function starIcon(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', STAR_PATH);
  svg.appendChild(path);
  return svg;
}

function canMark(): boolean {
  return risClient() === null || hasPerm(PERM.annotate);
}

/** The star of one viewport (only on image stacks, and only when the session may mark). */
function renderStar(viewport: Viewport): void {
  const root = viewport.overlayRoot;
  const address = imageAddress(viewport);
  let star = root.querySelector<HTMLButtonElement>('.key-image-star');
  const show = address !== undefined && canMark() && !hiddenOnThisDevice
    && (risClient() === null || isSessionStudy(address.series.studyInstanceUID));
  if (!show) {
    star?.remove();
    root.classList.remove('has-key-star');
    return;
  }
  if (!star) {
    star = document.createElement('button');
    star.type = 'button';
    star.className = 'key-image-star';
    star.appendChild(starIcon());
    star.addEventListener('mousedown', (event) => event.stopPropagation());
    star.addEventListener('dblclick', (event) => event.stopPropagation());
    star.addEventListener('click', (event) => {
      event.stopPropagation();
      void toggleKeyImage(viewport);
    });
    root.appendChild(star);
    root.classList.add('has-key-star');
  }
  const marked = markedAt(viewport) !== undefined;
  star.classList.toggle('marked', marked);
  star.setAttribute('aria-pressed', String(marked));
  const title = marked ? KEY_IMAGE_TEXT.unmark : KEY_IMAGE_TEXT.mark;
  if (star.title !== title) {
    star.title = title;
    star.setAttribute('aria-label', title);
  }
}

function refreshStars(): void {
  shellRef?.viewports().forEach((vp) => {
    if (vp.currentImage()) renderStar(vp);
  });
}

/** A key image stored from elsewhere (the Asistente's "Imagen clave"): the stars and the list show it at once. */
export function rememberKeyImage(studyUid: string, stored: KeyImage): void {
  byStudy.set(studyUid, [...(byStudy.get(studyUid) ?? []).filter((k) => k.id !== stored.id), stored]);
  refreshStars();
}

/** Back layer of a fused cell: the PET image drawn over the base, with its colour map and opacity. */
function backLayer(viewport: Viewport): Record<string, unknown> | null {
  const layer = fusionLayerInfo(viewport);
  if (!layer) return null;
  const instance = instanceAt(layer.series, layer.index);
  return {
    series_uid: layer.series.seriesInstanceUID,
    series_description: layer.series.description,
    sop_uid: instance?.sopInstanceUID ?? null,
    slice: layer.index + 1,
    colormap: layer.colormap,
    opacity: layer.opacity,
  };
}

/** Star click: mark the image on screen as key image, or unmark it. */
export async function toggleKeyImage(viewport: Viewport): Promise<void> {
  const shell = shellRef;
  const client = risClient();
  if (!shell) return;
  if (!client) {
    shell.toast(UNAVAILABLE_MESSAGE, 'warning');
    return;
  }
  const address = imageAddress(viewport);
  if (!address || busy) return;
  const uid = address.series.studyInstanceUID;
  const existing = markedAt(viewport);
  busy = true;
  try {
    if (existing) {
      await client.unmarkKeyImage(existing.id);
      byStudy.set(uid, (byStudy.get(uid) ?? []).filter((k) => k.id !== existing.id));
    } else {
      const layer = backLayer(viewport);
      const body: KeyImageIn = {
        series_uid: address.series.seriesInstanceUID,
        sop_uid: address.sop,
        frame: address.frame,
        slice: address.index + 1,
        series_description: address.series.description || null,
        is_fusion: layer !== null,
        back_layer: layer,
      };
      const stored = await client.markKeyImage(uid, body);
      byStudy.set(uid, [...(byStudy.get(uid) ?? []).filter((k) => k.id !== stored.id), stored]);
    }
  } catch (error) {
    console.warn('[viewer] key image change failed', error);
    shell.toast(existing ? KEY_IMAGE_TEXT.deleteError : KEY_IMAGE_TEXT.storeError, 'error');
  } finally {
    busy = false;
    refreshStars();
  }
}

// --- toolbar dropdown ---------------------------------------------------------------------------

function findSeries(shell: ViewerShell, uid: string): SeriesRef | undefined {
  for (const study of shell.studies()) {
    const series = study.series.find((s) => s.seriesInstanceUID === uid);
    if (series) return series;
  }
  return undefined;
}

function allKeyImages(shell: ViewerShell): KeyImage[] {
  return shell.studies().flatMap((study) => byStudy.get(study.studyInstanceUID) ?? []);
}

function keyImageLabel(shell: ViewerShell, key: KeyImage): string {
  const series = findSeries(shell, key.series_uid);
  const name = key.series_description || series?.description || (series ? `Serie ${series.seriesNumber}` : 'Serie');
  return key.slice ? `${name} · Corte ${key.slice}` : name;
}

function menuItems(shell: ViewerShell): MenuItem[] {
  const keys = allKeyImages(shell);
  if (keys.length === 0) return [{ id: 'key-image-none', label: KEY_IMAGE_TEXT.none }];
  const active = shell.activeViewport();
  const shown = active ? markedAt(active) : undefined;
  return keys.map((key): MenuItem => {
    const chosen = shown?.id === key.id;
    return {
      id: `key-image-${key.id}`,
      label: chosen ? `${keyImageLabel(shell, key)} (${KEY_IMAGE_TEXT.chosen})` : keyImageLabel(shell, key),
      title: chosen ? KEY_IMAGE_TEXT.chosen : keyImageLabel(shell, key),
      command: `keyimage:${key.id}` as CommandId,
      active: chosen,
    };
  });
}

function openDropdown(shell: ViewerShell): void {
  const button = document.getElementById('tb-key-images');
  if (!button) return;
  if (button.getAttribute('aria-expanded') === 'true') {
    closeMenus();
    return;
  }
  const rect = button.getBoundingClientRect();
  showMenu(shell.regions.overlayLayer, menuItems(shell), rect.left, rect.bottom + DROPDOWN_GAP_PX, { className: 'tb-menu tb-menu-key-images', idPrefix: 'tb-', owner: button });
}

/** Stack position of a key image in its series (SOP instance + frame, else its stored slice). */
function indexOf(series: SeriesRef, key: KeyImage): number {
  let offset = 0;
  for (const instance of series.instances) {
    if (instance.sopInstanceUID === key.sop_uid) return offset + Math.min(Math.max(1, key.frame), instance.numberOfFrames) - 1;
    offset += instance.numberOfFrames;
  }
  return key.slice ? key.slice - 1 : 0;
}

/** Shows a key image in the active cell, switching series when needed. */
async function goToKeyImage(shell: ViewerShell, id: string | undefined): Promise<void> {
  const key = allKeyImages(shell).find((k) => k.id === id);
  const series = key ? findSeries(shell, key.series_uid) : undefined;
  if (!key || !series) {
    shell.toast(KEY_IMAGE_TEXT.showError, 'error');
    return;
  }
  const index = indexOf(series, key);
  const viewport = shell.activeViewport();
  if (viewport?.currentStack?.series.seriesInstanceUID === series.seriesInstanceUID && viewport.currentStack.series.instances.length === series.instances.length) {
    if (viewport.currentIndex() !== index) window.cornerstoneTools.scrollToIndex(viewport.element, index);
    return;
  }
  await shell.showSeriesIn(shell.activeIndex(), series, index);
}

// --- loading -------------------------------------------------------------------------------------

async function loadFor(studies: StudyRef[]): Promise<void> {
  const client = risClient();
  if (!client) return;
  const uids = studies.map((s) => s.studyInstanceUID).filter((uid) => isSessionStudy(uid) && !byStudy.has(uid) && !loading.has(uid));
  await Promise.all(uids.map(async (uid) => {
    loading.add(uid);
    try {
      byStudy.set(uid, await client.keyImages(uid));
    } catch (error) {
      console.warn('[viewer] key images load failed', uid, error);
      shellRef?.toast(KEY_IMAGE_TEXT.retrieveError, 'error');
    } finally {
      loading.delete(uid);
    }
  }));
  refreshStars();
}

interface KeyImagesDebug {
  list(): Array<{ id: string; series_uid: string; sop_uid: string; frame: number; slice: number | null | undefined; is_fusion: boolean | undefined }>;
}

declare global {
  interface Window {
    __viewerKeyImages?: KeyImagesDebug;
  }
}

/** Overlay stars on every stack viewport (when `keyImagesModule`); list and navigation with ris. */
export function install(shell: ViewerShell): void {
  hiddenOnThisDevice = shell.config.mobileModule !== false && isMobileDevice();
  shellRef = shell;
  if (!viewerFlags(shell).keyImagesModule) return;
  shell.on('image-rendered', ({ viewport }) => renderStar(viewport));
  if (!risClient()) return;
  registerCommand('key-images', () => openDropdown(shell));
  registerCommand('keyimage', (id) => void goToKeyImage(shell, id));
  void loadFor(shell.studies());
  shell.on('studies-changed', ({ studies }) => void loadFor(studies));
  window.__viewerKeyImages = {
    list: () => allKeyImages(shell).map((k) => ({ id: k.id, series_uid: k.series_uid, sop_uid: k.sop_uid, frame: k.frame, slice: k.slice, is_fusion: k.is_fusion })),
  };
}

